import { Product } from '@/store/modules/product';
import store from '@/store';
import { ShaderSlot } from '../shaders/others/shader_slot';
import { Vector2, MeshBuilder } from '@babylonjs/core';
import { SlotElement } from '../slotElement';
import { positionManager } from '../positionManager';
import { ElementMenuItem } from '../elementMenuItem';
import Element, { SpecyficType, ElementType } from '../element';
import { placementManager } from '../placementManager';
import { BaseShape } from '../IShape';
import { IShader } from '../shaders/shader';

class RegionHelperManager {

    private get product(): Product {
        return store.getters.product;
    }

    private scenePreview: any;
    private body!: Element;
    private shader: IShader = new ShaderSlot();
    private material: any;
    private _helpers: SlotElement[] = [];

    public initialize(scenePreview: any) {
        this.scenePreview = scenePreview;
        this.body = scenePreview.elements.find((x: Element) =>
            x.type === ElementType.BODY,
        );
        this.material = this.scenePreview
            .getMaterial(this.shader, '');
    }

    public createHelpers() {
        for (let i = 1; i <= this.product.shape.x; i++) {
            for (let j = 1; j <= this.product.shape.y; j += 0.5) {
                this.createHelper(i, j, ElementType.INSIDE, SpecyficType.SHELF);
                if (!Number.isInteger(j)) { continue; }
                this.createHelper(i, j, ElementType.FRONT, SpecyficType.OTHER);
                this.createHelper(i, j, ElementType.INSIDE, SpecyficType.DRAWER);
                if (j === 1) { continue; }
                this.createHelper(i, j, ElementType.INSIDE, SpecyficType.HANGER);
            }
        }
    }

    public hideHelpers() {
        this._helpers.forEach((x) => x.isVisible = false);
    }

    public showFreeHelpers() {
        const element = store.getters.scene.selectedElementMenuItem;
        if (element.specyficType === SpecyficType.SHELF) {
            const offsetY = 0.5 * element.shape.y;
            this._showFreeHelpers(element, offsetY, 0.5);
        } else {
            const offsetY = 0.5 * (element.shape.y - 1);
            this._showFreeHelpers(element, offsetY, 1);
        }
    }

    private createHelper(
        x: number,
        y: number,
        type: ElementType,
        specyficType: SpecyficType,
    ) {
        const isShelf = specyficType === SpecyficType.SHELF;
        const isDrawer = specyficType === SpecyficType.DRAWER;
        const isHanger = specyficType === SpecyficType.HANGER;
        const isFront = type === ElementType.FRONT;
        const shape = new Vector2(1, isShelf ? 0 : 1);
        const position = new Vector2(x, y);
        const material = this.material;
        material.alpha = 0.6;
        const slotParams = this.getSlotParams(isShelf, isDrawer, isHanger, isFront);
        const slot = MeshBuilder.CreateBox(
            'slot',
            slotParams,
            this.scenePreview.scene,
        );
        slot.isPickable = false;
        slot.material = material;
        const slotElement = new SlotElement(position, shape, [slot], isShelf);
        slotElement.slotType = type;
        slotElement.slotSpecyficType = specyficType;
        this._helpers.push(slotElement);
        slotElement.isVisible = false;
        positionManager.position(slotElement);
        if (isFront) { this.correctFrontPosition(slotElement); }
    }

    private correctFrontPosition(slotElement: SlotElement) {
        slotElement.center.z -= this.body.depth / 2;
    }

    private getSlotParams(
        isShelf: boolean,
        isDrawer: boolean,
        isHanger: boolean,
        isFront: boolean,
    ): any {
        let height = 0;
        let width = 0;
        let depth = 0;
        if (isShelf) {
            height = 2;
            width = positionManager.cellWidth;
            depth = positionManager.cellDepth;
        }
        if (isDrawer) {
            height = positionManager.cellHeight;
            width = positionManager.cellWidth;
            depth = positionManager.cellDepth;
        }
        if (isHanger) {
            height = 3;
            width = positionManager.cellWidth;
            depth = 3;
        }
        if (isFront) {
            height = positionManager.cellHeight - 1;
            width = positionManager.cellWidth - 1;
            depth = 3;
        }
        height -= 0.1;
        width -= 0.2;
        depth -= 1;
        return { height, width, depth };
    }

    private _showFreeHelpers(
        element: ElementMenuItem,
        offsetY: number,
        jIncrement: number): SlotElement[] {
        const result: SlotElement[] = [];
        const offsetX = 0.5 * (element.shape.x - 1);
        const slots = this._helpers.filter((x) =>
            x.slotType === element.type &&
            x.slotSpecyficType === element.specyficType);
        for (let i = 1 + offsetX; i <= this.body.shape.x - offsetX; i++) {
            for (let j = 1 + offsetY; j <= this.body.shape.y - offsetY; j += jIncrement) {
                const position = new Vector2(i, j);
                if (placementManager.canPlace(position, element.shape, element.type, element.specyficType)) {
                    const test = new BaseShape(position, element.shape);
                    if (element.specyficType === SpecyficType.HANGER) {
                        result.push(...slots.filter((x) =>
                            x.inside(test) && x.position.y - 0.5 === test.position.y,
                        ));
                    } else {
                        result.push(...slots.filter((x) => x.inside(test)));
                    }
                }
            }
        }
        result.forEach((x) => x.isVisible = true);
        return result;
    }
}

export const regionHelperManager = new RegionHelperManager();
